import { useCurrentWorkspace } from '../../../hooks/useCurrentWorkspace';
import { useFilterConfigs } from '../../../hooks/useFilterConfigs';
import { useFilters } from '../../../hooks/useFilters';
import { workspaceTypeChecks } from '../../../lib/api/helpers';
import type { ExpressionType, Filter } from '../../../stores/filters';
import type { FilterConfigItem } from '../LeftView/components/FilterConfig/FilterConfigTypes';
import { useAdvancedFilters } from '../LeftView/hooks/useAdvancedFilters';
import { useAdvertisers } from '../LeftView/hooks/useAdvertisers';

export const useWorkspaceFilters = () => {
  const { parseConfigString, setProviderState } = useAdvancedFilters();
  const { currentWorkspace } = useCurrentWorkspace();
  const { advertisers } = useAdvertisers();

  const { filterConfigs } = useFilterConfigs({
    workspaceTypeId: currentWorkspace?.workspace_type_id,
    disableAutoFetch: !currentWorkspace?.workspace_type_id,
  });
  const { filters, setFilter } = useFilters();

  const loadFiltersForWorkspace = (
    workspace: typeof currentWorkspace,
    filtersToLoadFromTemplate: Record<string, string>,
    filterItemsOverride?: FilterConfigItem[],
  ) => {
    try {
      const effectiveFilterItems = filterItemsOverride ?? filterConfigs;

      const outputFilterArray: Filter[] = [];

      const workspaceAdvertiser = advertisers.find((a) => a.id === workspace?.workspace_advertiser_id);
      const isHCPSiteAnalytics = workspaceTypeChecks.isHCPSiteAnalytics(workspace?.workspace_type_id);

      const advertiserFilter: Filter = {
        filterName: 'advertiser_filter',
        expression: isHCPSiteAnalytics
          ? `${workspaceAdvertiser?.value} #${workspaceAdvertiser?.id}`
          : (workspaceAdvertiser?.value ?? ''),
        enabled: true,
        advertiserId: workspaceAdvertiser?.id,
        dashboardSlug: null,
      };

      for (const [filterName, expression] of Object.entries(filtersToLoadFromTemplate)) {
        const enabled = true;
        let expressionType: ExpressionType = 'target';

        const translatedFilterName = effectiveFilterItems?.find((f) => f.lookmlAddress === filterName)?.filterName;

        const exprStr = expression as string;
        const expressionsArray = exprStr.includes(',') ? exprStr.split(',').map((item) => item.trim()) : [exprStr];

        const filterConfig = effectiveFilterItems?.find((f) => f.filterName === translatedFilterName);

        if (exprStr === 'Yes' || exprStr === 'No') expressionType = 'yesno';
        else if (expressionsArray.every((item) => item.startsWith('-'))) expressionType = 'block';
        else if (exprStr.startsWith('-')) expressionType = 'block';
        else if (filterConfig?.type === 'range_slider' && exprStr.startsWith('not')) expressionType = 'block';

        if (translatedFilterName) {
          outputFilterArray.push({
            ...(translatedFilterName === 'advertiser_filter' ? advertiserFilter : {}),
            filterName: translatedFilterName,
            expression,
            enabled,
            expressionType,
          });
        }
      }

      if (!filters?.advertiser_filter && !outputFilterArray.find((f) => f.filterName === 'advertiser_filter')) {
        outputFilterArray.push(advertiserFilter);
      }

      outputFilterArray.forEach(setFilter);

      const globalConfigString = filtersToLoadFromTemplate['genome_hcp_template.config_string'];

      const facilityConfigString =
        filtersToLoadFromTemplate['genome_hcp_template.extend_with_facility_affiliation_config_string'];
      const extendConfigString = filtersToLoadFromTemplate['genome_hcp_template.extend_with_care_team_config_string'];

      setProviderState('global', parseConfigString(globalConfigString || ''));

      setProviderState('extend_with_facility_affiliation', parseConfigString(facilityConfigString || ''));
      setProviderState('extend_with_care_team', parseConfigString(extendConfigString || ''));

      return outputFilterArray;
    } catch (error) {
      console.error(error);
    }
  };

  return { loadFiltersForWorkspace };
};
